import { Reference } from '@ninetailed/experience.js';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';
import { INavigation, INavigationElement } from 'global';
import { parseExperiences } from 'utils/ninetailed';
import { NavigationType, TargetGroup } from '.';
import { NavigationElementConfig } from '../data';
import { ElementsByConfig } from '../use-navigation-data';
import { createReference, EMPTY_REFERENCE } from './empty-reference';

/**
 * Case-insensitive comparison of Contentful field values
 */
const isSameFieldValue = (a: string | undefined, b: string | undefined): boolean => {
  if (!a || !b) return false;

  return a.toLowerCase() === b.toLowerCase();
};

/**
 * Builds the lookup key of a navigation element config.
 * Values are upper-cased so the key is stable regardless of the casing coming from Contentful.
 */
export const getNavigationConfigKey = ({
  type,
  petType,
  targetGroup,
}: {
  type: string;
  petType: string;
  targetGroup: string;
}): string => {
  return `${type.toUpperCase()}_${petType.toUpperCase()}_${targetGroup.toUpperCase()}`;
};

/**
 * Helper function to find navigation element
 */
export const findNavigationElement = (
  elements: INavigationElement[] | undefined,
  config: NavigationElementConfig
): INavigationElement | null => {
  if (!elements) return null;

  return (
    elements.find(
      (element) =>
        isSameFieldValue(element.fields?.type, NavigationType[config.type]) &&
        isSameFieldValue(element.fields?.targetGroup, TargetGroup[config.targetGroup]) &&
        isSameFieldValue(element.fields?.petType, config.petType)
    ) || null
  );
};

/**
 * Helper function to create experiment baseline and experiences data
 */
export const createExperimentData = (
  element: INavigationElement | null
): {
  baseline: Reference;
  experiences: ExperienceConfiguration<Reference>[];
} => {
  return {
    baseline: createReference(element),
    experiences: element ? parseExperiences({ entry: element }) : [],
  };
};

/**
 * Helper function to process variant
 */
export const processVariant = (
  variant: unknown,
  originalElement: INavigationElement
): INavigationElement | null => {
  if (variant && typeof variant === 'object') {
    if ((variant as unknown as INavigationElement).fields) {
      return variant as INavigationElement;
    } else {
      return {
        ...originalElement,
        fields: variant,
      } as INavigationElement;
    }
  }
  return null;
};

/**
 * Helper function to get experiment data by config key
 */
export const getExperimentData = ({
  type,
  petType,
  targetGroup,
  elementsByConfig,
}: {
  type: string;
  petType: string;
  targetGroup: string;
  elementsByConfig: Map<string, ElementsByConfig>;
}): {
  baseline: Reference;
  experiences: ExperienceConfiguration<Reference>[];
  experimentName?: string;
} => {
  const key = getNavigationConfigKey({ type, petType, targetGroup });
  const elementData = elementsByConfig.get(key);
  const data = elementData?.experimentData;

  // Get experiment name from the element's title or fallback to config-based name
  const experimentName =
    elementData?.element?.fields?.title ||
    `${type.toLowerCase()}-${petType.toLowerCase()}-${targetGroup.toLowerCase()}`;

  return {
    baseline: data?.baseline || EMPTY_REFERENCE,
    experiences: (data?.experiences as ExperienceConfiguration<Reference>[]) || [],
    experimentName,
  };
};

/**
 * Clean navigation data to prevent circular reference errors while preserving A/B testing
 */
export const cleanNavigationForExperiments = (
  nav: INavigation | undefined
): INavigation | undefined => {
  if (!nav) return nav;

  try {
    // Test if it can be serialized first
    JSON.stringify(nav);
    return nav; // No circular references, return as-is
  } catch (error) {
    // Check if the error is due to circular references
    if (error instanceof Error && error.message.includes('Converting circular structure to JSON')) {
      console.warn(
        'Circular reference detected in navigation data, applying cleaning while preserving A/B testing'
      );

      try {
        return {
          ...nav,
          sys: nav.sys,
          fields: {
            ...nav.fields,
            // Remove the problematic arrays but keep structure
            nt_variants: [],
            nt_experiences: [],
          },
        } as INavigation;
      } catch (cleanError) {
        // If even cleaning fails, return minimal structure
        console.warn('Advanced cleaning failed, using minimal fallback');
        return {
          sys: { id: nav.sys?.id || '' },
          fields: {},
        } as INavigation;
      }
    }
  }
};
